import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../lib/api.js';
import { promptText } from '../lib/prompt-dialog.js';
import { isCollapsed, toggleCollapsed, visibleTree } from '../lib/tree.js';
import type { TreeEntry } from '../lib/tree.js';
import { assetKind } from '../state/project-files.js';
import { useWorkspace } from '../state/workspace.js';
import { ASSET_ICON } from './panels/Assets.js';
import { ContextMenu, type MenuItem } from './ui/controls.js';
import { Icon } from './ui/icons.js';

type DirEntry = TreeEntry;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function Explorer() {
  const projectId = useWorkspace((s) => s.projectId);
  const openFile = useWorkspace((s) => s.openFile);
  const tabs = useWorkspace((s) => s.tabs);
  const [entries, setEntries] = useState<DirEntry[]>([]);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [menu, setMenu] = useState<{ x: number; y: number; entry: DirEntry | null } | null>(null);
  const closeTab = useWorkspace((s) => s.closeTab);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      // Depth 4 gives a useful tree immediately without walking a huge repo.
      const result = await api.get<{ entries: DirEntry[] }>(
        `/projects/${projectId}/files?depth=4`,
      );
      setEntries(result.entries);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not list files.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = visibleTree(entries, collapsed, filter);

  /** Folder a new file goes into: the clicked folder, or the clicked file's folder. */
  function folderOf(entry: DirEntry | null): string {
    if (!entry) return '';
    if (entry.kind === 'directory') return entry.path;
    const i = entry.path.lastIndexOf('/');
    return i < 0 ? '' : entry.path.slice(0, i);
  }

  async function run(action: () => Promise<unknown>) {
    try {
      await action();
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The file operation failed.');
    }
    await load();
  }

  async function newFile(entry: DirEntry | null) {
    const dir = folderOf(entry);
    const name = await promptText(`New file in ${dir || 'the project root'}`, 'new_file.rhai');
    if (!name?.trim()) return;
    const path = dir ? `${dir}/${name.trim()}` : name.trim();
    await run(() => api.put(`/projects/${projectId}/file`, { path, content: '' }));
    void openFile(path);
  }

  async function newFolder(entry: DirEntry | null) {
    const dir = folderOf(entry);
    const name = await promptText(`New folder in ${dir || 'the project root'}`, 'new_folder');
    if (!name?.trim()) return;
    await run(() => api.post(`/projects/${projectId}/mkdir`, { path: dir ? `${dir}/${name.trim()}` : name.trim() }));
  }

  async function rename(entry: DirEntry) {
    const name = await promptText(`Rename ${entry.path}`, entry.name);
    if (!name?.trim() || name.trim() === entry.name) return;
    const dir = folderOf({ ...entry, kind: 'file' });
    const to = dir ? `${dir}/${name.trim()}` : name.trim();
    await run(() => api.post(`/projects/${projectId}/move`, { from: entry.path, to }));
    if (tabs.includes(entry.path)) {
      closeTab(entry.path);
      if (entry.kind !== 'directory') void openFile(to);
    }
  }

  async function remove(entry: DirEntry) {
    const what = entry.kind === 'directory' ? `the folder ${entry.path} and everything in it` : entry.path;
    if (!window.confirm(`Delete ${what}?`)) return;
    await run(() => api.del(`/projects/${projectId}/file?path=${encodeURIComponent(entry.path)}`));
    for (const t of tabs) if (t === entry.path || t.startsWith(`${entry.path}/`)) closeTab(t);
  }

  function menuItems(entry: DirEntry | null): MenuItem[] {
    const items: MenuItem[] = [
      { id: 'new-file', label: 'New file…', icon: 'plus', run: () => void newFile(entry) },
      { id: 'new-folder', label: 'New folder…', icon: 'folder', run: () => void newFolder(entry) },
    ];
    if (entry) {
      items.push(
        { id: 'sep', label: '', separator: true },
        { id: 'rename', label: 'Rename…', run: () => void rename(entry) },
        { id: 'delete', label: 'Delete', danger: true, run: () => void remove(entry) },
      );
    }
    return items;
  }

  function toggle(path: string) {
    setCollapsed((prev) => new Set(toggleCollapsed(prev, path)));
  }

  if (!projectId) {
    return <p className="muted explorer-hint">No project open.</p>;
  }

  return (
    <div className="explorer">
      <div className="explorer-head">
        <input
          className="explorer-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter files"
          aria-label="Filter files"
        />
        <button className="icon-btn" onClick={() => void newFile(null)} title="New file">
          <Icon name="plus" size={13} />
        </button>
        <button className="icon-btn" onClick={() => void load()} title="Refresh" disabled={loading}>
          <Icon name="refresh" size={13} />
        </button>
      </div>

      {error && <p className="login-error">{error}</p>}

      <ul
        className="tree"
        role="tree"
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY, entry: null });
        }}
      >
        {visible.map(({ entry, depth }) => {
          const isDir = entry.kind === 'directory';
          const isOpen = tabs.includes(entry.path);
          return (
            <li key={entry.path}>
              <button
                type="button"
                className={`tree-row${isOpen ? ' is-open' : ''}`}
                style={{ paddingLeft: 8 + depth * 12 }}
                onClick={() => {
                  if (isDir) toggle(entry.path);
                  else void openFile(entry.path);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenu({ x: e.clientX, y: e.clientY, entry });
                }}
                title={entry.path}
              >
                <span className="tree-caret">
                  {isDir && <Icon name={isCollapsed(collapsed, entry.path) ? 'chevronRight' : 'chevronDown'} size={11} />}
                </span>
                <span className={`tree-icon kind-${assetKind(entry)}`} aria-hidden="true">
                  <Icon
                    name={entry.kind === 'symlink' ? 'link' : isDir ? (isCollapsed(collapsed, entry.path) ? 'folder' : 'folderOpen') : ASSET_ICON[assetKind(entry)]}
                    size={14}
                  />
                </span>
                <span className="tree-name">{entry.name}</span>
                {!isDir && <span className="tree-size">{formatSize(entry.size)}</span>}
              </button>
            </li>
          );
        })}
        {!loading && visible.length === 0 && (
          <li className="muted tree-empty">
            {filter ? 'No matching files.' : 'This project folder is empty.'}
          </li>
        )}
      </ul>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.entry)} onClose={() => setMenu(null)} />}
    </div>
  );
}